/**
 * Ported from the lekterion codebase (`src/lib/corpus.ts`) @ `d942f2f`.
 * Bindery is canonical for this module from 2026-09-06; reconcile source-side
 * drift toward this copy.
 */
import { useResource, type Resource } from "@bindery/ui/hooks/use-resource";

import {
  CorpusCatalog,
  DocumentModel,
  type TextManifest,
} from "@/lib/schemas/model";

/**
 * Tier-1 loaders (DATA-ARCHITECTURE.md §1.6). The document model and catalog are
 * static JSON served from Pages (`public/corpus/`, produced by ingest). Both are
 * Zod-validated on load — the boundary where untrusted JSON becomes typed data.
 */

const BASE = import.meta.env.BASE_URL;

export async function loadCatalog(): Promise<CorpusCatalog> {
  const res = await fetch(`${BASE}corpus/catalog.json`);
  if (!res.ok)
    throw new Error(`Failed to load catalog (${String(res.status)})`);
  return CorpusCatalog.parse(await res.json());
}

export async function loadModel(textId: string): Promise<DocumentModel> {
  const res = await fetch(`${BASE}corpus/${textId}.json`);
  if (!res.ok)
    throw new Error(`Text "${textId}" not found (${String(res.status)})`);
  return DocumentModel.parse(await res.json());
}

function toError(e: unknown): Error {
  return e instanceof Error ? e : new Error(String(e));
}

function withErrorObject<T>(resource: Resource<T>): Resource<T, Error> {
  return resource.status === "error"
    ? { status: "error", error: toError(resource.error) }
    : resource;
}

export function useCatalog(): Resource<CorpusCatalog, Error> {
  return withErrorObject(useResource(loadCatalog, []).resource);
}

/** The sibling texts of one work, in reading order (§2 forward/back navigation). */
export interface WorkSiblings {
  work: string;
  list: TextManifest[];
  index: number;
  prev: TextManifest | null;
  next: TextManifest | null;
}

/**
 * Texts in the same work as `textId`, in catalog (reading) order, with the
 * previous / next neighbour resolved. `null` if the id isn't in the catalog.
 */
export function workSiblings(
  catalog: CorpusCatalog,
  textId: string,
): WorkSiblings | null {
  const self = catalog.texts.find((t) => t.id === textId);
  if (!self) return null;
  const list = catalog.texts.filter(
    (t) => t.author === self.author && t.work === self.work,
  );
  const index = list.findIndex((t) => t.id === textId);
  return {
    work: self.work,
    list,
    index,
    prev: index > 0 ? (list[index - 1] ?? null) : null,
    next: index < list.length - 1 ? (list[index + 1] ?? null) : null,
  };
}

export function useDocumentModel(
  textId: string | undefined,
): Resource<DocumentModel, Error> {
  const { resource } = useResource(() => {
    if (!textId) return Promise.reject(new Error("No text id in the URL"));
    return loadModel(textId);
  }, [textId]);
  return withErrorObject(resource);
}
